import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAdminAuth } from '../AdminAuthContext';
import { adminLogin } from '../adminApi';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock } from 'lucide-react';

export default function LoginPage() {
  const { login, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      login(data.access_token);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Giriş başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 mb-2">
            <span className="text-[#c5a059] font-bold text-3xl tracking-[0.2em] uppercase">OBER</span>
            <span className="text-white/25 text-sm uppercase tracking-widest">Yapı</span>
          </div>
          <p className="text-white/25 text-xs uppercase tracking-[0.2em]">Admin Paneli</p>
        </div>

        {/* Card */}
        <div className="bg-[#111] border border-white/[0.07] rounded-2xl p-8">
          <div className="flex items-center gap-2.5 mb-7">
            <div className="w-8 h-8 rounded-full bg-[#c5a059]/10 border border-[#c5a059]/20 flex items-center justify-center">
              <Lock size={13} className="text-[#c5a059]" />
            </div>
            <h1 className="text-white font-semibold text-sm">Yönetici Girişi</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
                E-posta
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@oberyapi.com"
                required
                autoFocus
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 focus:bg-white/[0.06] transition-all"
              />
            </div>

            <div>
              <label className="block text-[10px] text-white/35 mb-1.5 uppercase tracking-[0.15em]">
                Şifre
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-2.5 pr-10 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#c5a059]/50 focus:bg-white/[0.06] transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/50 transition-colors"
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#c5a059] hover:bg-[#d4af6a] active:bg-[#b8944e] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold py-2.5 rounded-lg text-sm transition-colors mt-1"
            >
              {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
            </button>
          </form>
        </div>

        <p className="text-center text-white/15 text-xs mt-6">
          Ober Yapı © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
